import * as THREE from 'three';
import { basic, emis, metal, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Ragna, the Horn (docs/ANTIHEROES.md): she went out through the gate to hunt them, alone, and never came back. The
 * titan dreams her lean and quick, in scraps of black glass armour — a single great pauldron, bracers, greaves —
 * over hide gone dark as soot, cracked with a crimson light; long braids of glass down her back, a circlet with two
 * short horns, two crimson sparks for eyes. A great axe in her right hand, its edge burning; in her left the war horn
 * she blew at the gate, banded in iron. A mantle of smoke, ragged as a pelt, off her shoulders.
 *
 * Built on the fallen sentinel's bones (anim/creatures/ragna.ts reuses its walk, reel, stagger and fall), with the
 * same names as Corvin's: body, chest, head, flapF/flapB, armL/foreL/shield (the horn), armR/foreR/sword (the axe),
 * legs (thigh, shin). Faces +Z, `R` limbs on +X.
 */

/** Model height (the circlet's horns). */
export const RAGNA_HEIGHT = 2.15;

export const ragna = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 1213);
  const { g, rng } = c;
  const k4 = kit(rng);

  const glass = metal('#2c303b', { roughness: 0.16, metalness: 0.5, bumpScale: 0.01 });
  const glassD = metal('#1a1c23', { roughness: 0.28, metalness: 0.5, bumpScale: 0.02 });
  const hide = std('#1e1719', { roughness: 0.9, bumpScale: 0.07 });
  const iron = metal('#3a3d44', { roughness: 0.55, metalness: 0.6, bumpScale: 0.04 });
  const bone_ = std('#8f8a86', { roughness: 0.7, bumpScale: 0.04 });
  const wood = std('#1c1410', { roughness: 0.9, bumpScale: 0.08 });
  const blood = emis('#ffb0c4', '#ff1f50', 2.8);
  const bloodF = emis('#ffb0c4', '#ff3a6a', 2.0, { transparent: true, opacity: 0.85, depthWrite: false });
  const smokeM = basic('#24141a', 0.55);

  const bone = (parent: THREE.Object3D, at: THREE.Vector3, name: string): { p: THREE.Group; abs: THREE.Group } => joint(parent, at, name);

  /* ---------- legs: long, glass greaves over hide, wrapped boots ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = bone(g, V(k * 0.13, 0.98, 0), `leg${s}`);
    const hip = legB.abs;
    limb(hip, V(k * 0.13, 1.0, 0), V(k * 0.14, 0.56, 0.02), 0.11, 0.085, hide, 7);
    const shinB = bone(hip, V(k * 0.14, 0.55, 0.02), `shin${s}`);
    const lo = shinB.abs;
    k4.swell(lo, glassD, k * 0.14, 0.55, 0.07, 0.07, 0.075, 0.065, 7, 4);
    limb(lo, V(k * 0.14, 0.53, 0.02), V(k * 0.14, 0.12, 0.0), 0.085, 0.065, glass, 7);
    k4.crack(lo, [V(k * 0.14, 0.47, 0.085), V(k * 0.16, 0.34, 0.075), V(k * 0.13, 0.22, 0.07)], 0.007, blood, 0.03);
    const boot = M(new THREE.BoxGeometry(0.13, 0.1, 0.24), hide, lo);
    boot.position.set(k * 0.14, 0.05, 0.04);
    for (const y of [0.13, 0.2]) {
      const wrap = M(new THREE.TorusGeometry(0.07, 0.012, 3, 10), iron, lo);
      wrap.rotation.x = Math.PI / 2;
      wrap.position.set(k * 0.14, y, 0.0);
    }
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  /* ---------- hips: a hide kilt in strips, a belt of iron plates, the flaps ---------- */
  const bodyB = bone(g, V(0, 1.0, 0), 'body');
  const body = bodyB.abs;
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const strip = M(new THREE.BoxGeometry(0.11, 0.34, 0.02), hide, body);
    strip.position.set(Math.sin(a) * 0.23, 0.86, Math.cos(a) * 0.2);
    strip.rotation.y = a;
    strip.rotation.x = 0.12;
  }
  const belt = M(new THREE.TorusGeometry(0.24, 0.03, 4, 12), iron, body);
  belt.rotation.x = Math.PI / 2;
  belt.position.set(0, 1.04, 0);
  belt.scale.set(1, 0.8, 1);
  const flapF = bone(body, V(0, 1.02, 0.2), 'flapF');
  k4.panel(flapF.abs, V(0, 1.03, 0.22), 0.22, 0.5, 0.02, 1, hide, 0.6);
  const flapB = bone(body, V(0, 1.02, -0.2), 'flapB');
  k4.panel(flapB.abs, V(0, 1.03, -0.22), 0.28, 0.6, 0.02, -1, hide, 0.6);

  /* ---------- chest: a glass cuirass, a bare crack of crimson, one great pauldron on the axe side ---------- */
  const chestB = bone(body, V(0, 1.08, 0), 'chest');
  const chest = chestB.abs;
  k4.swell(chest, glass, 0, 1.33, 0.0, 0.25, 0.27, 0.18, 9, 6);
  k4.swell(chest, hide, 0, 1.13, 0.0, 0.22, 0.12, 0.16, 8, 4);
  k4.crack(chest, [V(-0.14, 1.52, 0.13), V(-0.06, 1.42, 0.17), V(0.02, 1.33, 0.18), V(0.0, 1.22, 0.17), V(0.09, 1.14, 0.15)], 0.012, blood, 0.05);
  glowSprite(chest, '#ff3a6a', 0.38, V(0.0, 1.34, 0.22), 0.5);
  M(new THREE.CylinderGeometry(0.09, 0.12, 0.08, 8), glassD, chest).position.set(0, 1.57, 0);
  {
    // The great pauldron (right): stacked glass lames and a ridge of shards.
    const pd = G(chest, 0.31, 1.53, 0);
    pd.rotation.z = -0.4;
    const dome = M(new THREE.SphereGeometry(1, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), glass, pd);
    dome.scale.set(0.2, 0.15, 0.2);
    for (let i = 1; i <= 3; i++) {
      const lame = M(new THREE.CylinderGeometry(0.2 - i * 0.012, 0.21 - i * 0.01, 0.05, 8, 1, true), i % 2 ? glassD : glass, pd);
      lame.position.y = -0.03 - i * 0.045;
    }
    for (let i = 0; i < 3; i++) {
      const sp = M(new THREE.ConeGeometry(0.03, 0.18 - i * 0.03, 4), glass, pd);
      sp.position.set(0.02 + i * 0.05, 0.13 - i * 0.03, -0.04 + i * 0.03);
      sp.rotation.z = -0.5;
    }
    // A strap of hide across to the other shoulder.
    const strap = M(new THREE.BoxGeometry(0.06, 0.62, 0.03), hide, chest);
    strap.position.set(0.0, 1.36, 0.185);
    strap.rotation.z = -0.7;
  }
  // The bare left shoulder: hide and an iron ring.
  k4.swell(chest, hide, -0.27, 1.5, 0, 0.11, 0.09, 0.11, 7, 4);

  /* ---------- the mantle of smoke, ragged as a pelt ---------- */
  const mantle = G(chest, 0, 1.58, -0.16);
  for (const [x, w, h, rz] of [[-0.15, 0.26, 1.15, 0.08], [0.15, 0.26, 1.1, -0.08], [0, 0.3, 1.25, 0]] as const) {
    const pn = k4.panel(mantle, V(x, 0, -0.02), w, h, 0.02, -1, smokeM, 0.7);
    pn.rotation.set(0.14, 0, rz);
  }

  /* ---------- head: a face of glass, two sparks for eyes, a circlet with two horns, long braids ---------- */
  const headB = bone(chest, V(0, 1.61, 0.01), 'head');
  const head = headB.abs;
  const skull = M(new THREE.SphereGeometry(0.12, 10, 8), glass, head);
  skull.position.set(0, 1.76, 0.01);
  skull.scale.set(0.95, 1.12, 1);
  const jaw = M(new THREE.BoxGeometry(0.14, 0.08, 0.1), glassD, head);
  jaw.position.set(0, 1.66, 0.05);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.02, 6, 4), blood, head).position.set(k * 0.045, 1.77, 0.11);
    glowSprite(head, '#ff6a8a', 0.2, V(k * 0.045, 1.77, 0.14), 0.95);
  }
  // War paint that glows: a crimson bar across the eyes.
  M(new THREE.BoxGeometry(0.2, 0.018, 0.02), blood, head).position.set(0, 1.795, 0.105);
  const circ = M(new THREE.TorusGeometry(0.125, 0.015, 4, 14), iron, head);
  circ.rotation.x = Math.PI / 2;
  circ.position.set(0, 1.84, 0.01);
  for (const k of [-1, 1]) {
    const hn = G(head, k * 0.1, 1.86, 0.0);
    hn.rotation.z = -k * 0.55;
    hn.rotation.x = -0.25;
    const horn = M(new THREE.ConeGeometry(0.03, 0.22, 6), bone_, hn);
    horn.position.y = 0.1;
    M(new THREE.ConeGeometry(0.012, 0.05, 4), blood, hn).position.y = 0.22;
  }
  // Hair: a mass of glass at the back and two long braids down it, a burning tip on each.
  const mane = M(new THREE.SphereGeometry(0.13, 8, 6), glassD, head);
  mane.position.set(0, 1.76, -0.05);
  mane.scale.set(1, 1.05, 0.9);
  const tips: THREE.Mesh[] = [];
  for (const k of [-1, 1]) {
    limb(head, V(k * 0.07, 1.74, -0.12), V(k * 0.1, 1.38, -0.2), 0.035, 0.025, glassD, 5);
    limb(head, V(k * 0.1, 1.38, -0.2), V(k * 0.11, 1.12, -0.22), 0.025, 0.018, glassD, 5);
    const t = M(new THREE.ConeGeometry(0.03, 0.09, 5), bloodF, head);
    t.position.set(k * 0.11, 1.07, -0.22);
    t.rotation.x = Math.PI;
    tips.push(t);
  }

  /* ---------- left arm: the war horn, banded in iron, a crimson glow in its mouth ---------- */
  const armLB = bone(chest, V(-0.3, 1.5, 0), 'armL');
  const aL = armLB.abs;
  limb(aL, V(-0.3, 1.5, 0), V(-0.34, 1.2, 0.04), 0.07, 0.06, hide, 6);
  const foreLB = bone(aL, V(-0.34, 1.19, 0.05), 'foreL');
  const fL = foreLB.abs;
  limb(fL, V(-0.34, 1.19, 0.05), V(-0.27, 1.0, 0.24), 0.065, 0.055, glass, 6);
  k4.crack(fL, [V(-0.33, 1.15, 0.12), V(-0.3, 1.08, 0.18)], 0.007, blood, 0.02);
  const shieldB = bone(fL, V(-0.26, 0.98, 0.27), 'shield');
  const sh = shieldB.abs;
  {
    M(new THREE.SphereGeometry(0.055, 6, 5), hide, sh).position.set(-0.26, 0.98, 0.27);
    // The horn: a long curved cone of bone from mouthpiece to bell.
    const hg = G(sh, -0.26, 0.98, 0.27);
    const pts = [V(0.0, 0.08, -0.12), V(0.03, 0.02, 0.0), V(0.02, -0.06, 0.14), V(-0.04, -0.06, 0.28)];
    for (let i = 0; i < pts.length - 1; i++) limb(hg, pts[i] as THREE.Vector3, pts[i + 1] as THREE.Vector3, 0.02 + i * 0.022, 0.035 + i * 0.024, bone_, 8);
    const bell = M(new THREE.CylinderGeometry(0.1, 0.075, 0.06, 10, 1, true), iron, hg);
    bell.position.set(-0.05, -0.06, 0.3);
    bell.rotation.x = Math.PI / 2;
    const mouth = M(new THREE.CircleGeometry(0.075, 10), blood, hg);
    mouth.position.set(-0.05, -0.06, 0.295);
    for (let i = 0; i < 2; i++) {
      const band = M(new THREE.TorusGeometry(0.045 + i * 0.02, 0.01, 3, 10), iron, hg);
      band.position.copy((pts[i + 1] as THREE.Vector3).clone());
      band.rotation.x = Math.PI / 2;
    }
    glowSprite(hg, '#ff3a6a', 0.3, V(-0.05, -0.06, 0.34), 0.55);
  }

  /* ---------- right arm: a great axe, its bearded blade of black glass with a burning edge ---------- */
  const armRB = bone(chest, V(0.31, 1.5, 0), 'armR');
  const aR = armRB.abs;
  limb(aR, V(0.32, 1.5, 0), V(0.37, 1.2, 0.02), 0.075, 0.065, hide, 6);
  const foreRB = bone(aR, V(0.37, 1.19, 0.02), 'foreR');
  const fR = foreRB.abs;
  limb(fR, V(0.37, 1.19, 0.02), V(0.39, 0.94, 0.1), 0.068, 0.058, glass, 6);
  for (const y of [1.1, 1.0]) {
    const r = M(new THREE.TorusGeometry(0.065, 0.012, 3, 10), iron, fR);
    r.position.set(0.38, y, 0.06);
    r.rotation.x = Math.PI / 2 - 0.4;
  }
  const swordB = bone(fR, V(0.39, 0.9, 0.11), 'sword');
  const sw = swordB.abs;
  M(new THREE.BoxGeometry(0.085, 0.095, 0.095), hide, sw).position.set(0.39, 0.9, 0.11);
  {
    const ax = G(sw, 0.39, 0.9, 0.11);
    ax.rotation.set(-0.3, 0, -0.12);
    M(new THREE.CylinderGeometry(0.024, 0.026, 1.25, 6), wood, ax).position.y = 0.42;
    for (const y of [0.0, 0.85]) M(new THREE.CylinderGeometry(0.032, 0.032, 0.06, 6), iron, ax).position.y = y;
    // The blade: a bearded half-moon of glass on one side, a spike behind.
    const blade = new THREE.Shape();
    blade.moveTo(0, 0.12);
    blade.quadraticCurveTo(0.2, 0.2, 0.32, 0.3);
    blade.quadraticCurveTo(0.24, 0.0, 0.32, -0.24);
    blade.quadraticCurveTo(0.16, -0.12, 0, -0.08);
    blade.lineTo(0, 0.12);
    const bg = new THREE.ExtrudeGeometry(blade, { depth: 0.03, bevelEnabled: false });
    bg.translate(0.02, 0, -0.015);
    M(bg, glass, ax).position.y = 0.92;
    // The burning edge along the moon.
    k4.crack(ax, [V(0.33, 1.22, 0), V(0.27, 1.07, 0), V(0.26, 0.93, 0), V(0.33, 0.68, 0)], 0.012, blood, 0.01);
    const spike = M(new THREE.ConeGeometry(0.03, 0.16, 4), glassD, ax);
    spike.position.set(-0.08, 0.94, 0);
    spike.rotation.z = Math.PI / 2;
    glowSprite(ax, '#ff3a6a', 0.4, V(0.26, 0.95, 0.04), 0.45);
  }

  /* ---------- smoke breathing off her ---------- */
  const wisps: Ribbon[] = [];
  for (const [x, z, h, y] of [[0.26, -0.18, 0.65, 1.5], [-0.24, -0.2, 0.6, 1.45], [0.04, -0.28, 0.8, 1.25], [-0.1, -0.26, 0.55, 0.85]] as const) {
    wisps.push(new Ribbon(chest, wisp(x, z, h, 4).map((p) => p.add(V(0, y, 0))), 0.045, smokeM, rng, 0.06));
  }

  const t = (n: number): void => {
    for (const r of wisps) r.step();
    tips.forEach((f, i) => {
      const k = ((n * (3 + i * 2) + i * 5) % 7) / 7;
      f.scale.set(0.85 + 0.3 * k, 0.8 + 0.5 * k, 0.85 + 0.3 * k);
    });
  };
  t(0);
  return done(c, RAGNA_HEIGHT, t, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p, flapF: flapF.p, flapB: flapB.p,
    armL: armLB.p, foreL: foreLB.p, shield: shieldB.p, armR: armRB.p, foreR: foreRB.p, sword: swordB.p,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!,
  }));
};
